import { useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { UpdateAccountModalErrors } from '../UpdateAccountModalErrors'
import { UpdateAccountModalActions } from '../UpdateAccountModalActions'

import { MyTextField } from 'components/ReactHookForm/MyTextField'
import { PasswordGuidelines } from './PasswordGuidelines'
import { createSchema, EditPasswordFormData } from 'models/forms/PasswordForm'
import { UpdateAccountServerErrors } from 'hooks/useUpdateAccountHandler'

export type PasswordModalProps = {
  loading?: boolean
  onClose: () => void
  onSubmit: (formData: EditPasswordFormData) => void
  serverErrors: UpdateAccountServerErrors
}
export const PasswordModalForm = ({
  loading,
  onClose,
  onSubmit,
  serverErrors
}: PasswordModalProps) => {
  const { t } = useTranslation('editPassword')

  const schema = useMemo(() => createSchema(t), [t])
  const methods = useForm<EditPasswordFormData>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema)
  })

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <fieldset className="ds-c-fieldset">
          <legend className="ds-c-label">
            <h2 className="ds-text-heading--xl">{t(`createNewHeading`)}</h2>
          </legend>
          <MyTextField
            name="password"
            label={t(`password.label`)}
            labelClassName="my-u-text-transform--default"
            defaultValue=""
          />
          <MyTextField
            name="confirm"
            label={t(`confirmPassword.label`)}
            labelClassName="my-u-text-transform--default"
            defaultValue=""
          />
        </fieldset>

        <PasswordGuidelines className="my-u-mt--14" />

        <UpdateAccountModalErrors
          serverErrors={serverErrors}
          basicErrorText={t('errors.server-basic')}
          invalidCriteriaErrorText={t('errors.server-validation')}
        />
        <UpdateAccountModalActions
          loading={loading}
          submitButtonText={t('submit')}
          submitSiteActivityProps={{
            core: {
              component: 'Account Settings',
              elementId: 'accountsettings_passwordsave_click',
              coreClickData: {
                linkText: t('submit')
              }
            }
          }}
          cancelButtonText={t('cancel')}
          handleCancel={onClose}
        />
      </form>
    </FormProvider>
  )
}
